import './ManageEmployee.scss';
import 'react-datepicker/dist/react-datepicker.css';
import PropTypes from 'prop-types';
import moment from 'moment';
import useEffectWithDebounce from '../../hooks/useEffectWithDebounce';
import React, {memo, useCallback, useMemo, useState} from 'react';
import {batch, useDispatch, useSelector} from 'react-redux';
import {FmlxButton, FmlxIcon, FmlxTable, FmlxTextBox} from 'fmlx-common-ui';
import {RESPONSE_STATUS} from '../../const/dataEnum';
import {FMLX_BUTTON, FMLX_COMPONENT, FMLX_TABLE} from '../../const/FmlxComponentEnum';
import {deleteEmployee, getEmployee, openModal} from '../../stores/business/manageEmployeeBusiness';
import {ModalType, TabId, tableDetail} from './enum';
import {tableEmployeeStateSelector} from '../../stores/selectors/manageEmployeeStateSelector';
import {setFilterMap, setPageNo, setPageSize, setSortValue} from '../../stores/actions/manageEmployeeAction';

const TableEmployee = ({tableId}) => {
  const {employees, sortValue, filterMap, pageIndex, pageSize, pageNo, totalItem, responseStatus, selectedTab} =
    useSelector(tableEmployeeStateSelector);
  const [onSearchQuery, setOnSearchQuery] = useState('');
  const dispatch = useDispatch();
  // const timer = useRef(null);

  const pageSizes = [10, 20, 30, 40, 50];
  const minLength = 3;
  const maxLength = 50;

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      const valueObject = {
        value: e.target.value,
        minLength,
        maxLength,
      };
      onEnterKeyPressed && onEnterKeyPressed(valueObject);
    }
  };

  const onEnterKeyPressed = ({value, minLength, maxLength}) => {
    if (value.length >= minLength && value.length <= maxLength) {
      setOnSearchQuery(value);
    }
  };

  const generateColumns = () => {
    let defaultColumns = [
      {
        Header: tableDetail.NAME.TITLE,
        accessor: 'fullName',
        editable: false,
        disableSort: false,
        hideSort: false,
        headerStyle: {
          width: '250px',
        },
        filterDefinition: {
          type: FMLX_TABLE.FILTER_TYPE.TEXT,
          component: <FmlxTextBox defaultValue={onSearchQuery} onKeyPress={handleKeyPress}/>,
        },
      },
      {
        Header: tableDetail.DEPARTMENT.TITLE,
        accessor: (d) => {
          return d.practice.department.departmentName;
        },
        editable: false,
        disableSort: false,
        hideSort: false,
        filterDefinition: {
          type: FMLX_TABLE.FILTER_TYPE.TEXT,
        },
      },
      {
        Header: tableDetail.PRACTICE.TITLE,
        accessor: (d) => {
          return d.practice.practiceName;
        },
        filterDefinition: {
          type: FMLX_TABLE.FILTER_TYPE.TEXT,
        },
        editable: false,
        disableSort: false,
        hideSort: false,
      },
      //employee office
      {
        Header: tableDetail.COMPANY_ID.TITLE,
        accessor: (d) => {
          return d.company.companyName;
        },
        filterDefinition: {
          type: FMLX_TABLE.FILTER_TYPE.TEXT,
        },
        editable: false,
        disableSort: false,
        hideSort: false,
      },
      {
        Header: tableDetail.ROLE_LEVEL.TITLE,
        accessor: (d) => {
          return d.roleLevel.roleLevelName;
        },
        hideFilter: true,
        editable: false,
        disableSort: true,
        hideSort: true,
      },
      {
        Header: tableDetail.JOIN_DATE.TITLE,
        accessor: 'joinDate',
        editable: false,
        hideFilter: true,
        disableSort: true,
        hideSort: true,
        Cell: ({value}) => {
          return !value ? '-' : moment(new Date(value)).local().format('DD MMM YYYY');
        },
      },
      {
        Header: tableDetail.NOTES.TITLE,
        accessor: 'notes',
        editable: false,
        hideFilter: true,
        disableSort: true,
        hideSort: true,
      },
      {
        Header: 'Action',
        accessor: (d) => {
          return d;
        },
        editable: false,
        disableSort: true,
        hideSort: true,
        hideFilter: true,
        Cell: ({value}) => {
          return (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '16px',
              }}
            >
              <FmlxButton
                size={FMLX_COMPONENT.SIZE.M}
                type={FMLX_BUTTON.TYPE.PRIMARY}
                variant={FMLX_BUTTON.VARIANT.CONTAIN}
                label=""
                onClick={() => {
                  onEditClickHandler(value);
                }}
                onlyIcon={true}
                icon={<FmlxIcon name={'PencilBorder'} size={FMLX_COMPONENT.SIZE.M}/>}
                withTooltip={false}
                tooltipPlacement={'top'}
                tooltipDescription={'Edit Employee'}
              />
              <FmlxButton
                size={FMLX_COMPONENT.SIZE.M}
                type={FMLX_BUTTON.TYPE.DANGER}
                variant={FMLX_BUTTON.VARIANT.CONTAIN}
                label=""
                onClick={() => onDeleteClickHandler(value)}
                onlyIcon={true}
                icon={<FmlxIcon name={'Trash'} size={FMLX_COMPONENT.SIZE.M}/>}
                withTooltip={false}
                tooltipPlacement={'top'}
                tooltipDescription={'Delete Employee'}
              />
            </div>
          );
        },
      },
    ];

    if (tableId === TabId.INACTIVE_EMPLOYEE) {
      /** replace column join date with last date */
      defaultColumns.splice(4, 1, {
        Header: tableDetail.LAST_DATE.TITLE,
        accessor: 'lastDay',
        editable: false,
        hideFilter: true,
        disableSort: true,
        hideSort: true,
        Cell: ({row}) => {
          const lastDay = row.original.lastDay;
          return !lastDay ? '-' : moment(new Date(lastDay)).local().format('DD MMM YYYY');
        },
      });
    }

    return defaultColumns;
  };

  const columns = generateColumns();

  useEffectWithDebounce(
    () => {
      dispatch(getEmployee(onSearchQuery));
    },
    [filterMap, sortValue, pageNo, pageSize, selectedTab, onSearchQuery],
    1000
  );

  const onDeleteClickHandler = async (employee) => {
    await dispatch(deleteEmployee(employee));
  };

  const onEditClickHandler = async (employee) => {
    await dispatch(openModal(ModalType.UPDATE, employee));
  };

  const fetchData = useCallback(
    ({pageSize, pageIndex}) => {
      batch(() => {
        dispatch(setPageSize(pageSize));
        dispatch(setPageNo(pageIndex + 1));
      });
    },
    [dispatch]
  );
  const getSortValue = ({type, column}) => {
    if (type != null && column != null) {
      dispatch(setSortValue({sortBy: column.toLowerCase(), sortType: type}));
    }
  };

  const getFilterMap = (value) => {
    dispatch(setFilterMap(value));
  };

  const memoizedDataSource = useMemo(() => employees, [employees]);
  /**
   * https://kentcdodds.com/blog/stop-using-isloading-booleans
   * const isLoading = status === 'idle' || status === 'pending'
   * const isResolved = status === 'resolved'/'success'
   * const isRejected = status === 'rejected'/'error'
   */
  const employeeComponent = (
    <FmlxTable
      id={'tableEmployee'}
      data={memoizedDataSource}
      columns={columns}
      getSortValue={getSortValue}
      getFilterMap={getFilterMap}
      pageSizes={pageSizes}
      placeholder={
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={{fontSize: 50}}>
            <FmlxIcon name="FileCopy" customColor="black"/>
          </div>
          <br/>
          <div>No Data</div>
        </div>
      }
      isLoading={responseStatus === RESPONSE_STATUS.IDLE || responseStatus === RESPONSE_STATUS.PENDING}
      showFilter
      showPagination={true}
      showToolbar={false}
      itemsTotal={totalItem}
      fetchData={fetchData}
      pageIndex={pageIndex}
      onRowDoubleClick={null}
    />
  );

  return <div className="manage-employee--table">{employeeComponent}</div>;
};

TableEmployee.propTypes = {
  tableId: PropTypes.number,
};

TableEmployee.defaultProps = {
  tableId: TabId.ACTIVE_EMPLOYEE,
};

export default memo(TableEmployee);
